<script setup lang="ts">
import { inject, onMounted, ref, Ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { LANG_ENGLISH, STATUS_OPEN, STATUS_CLOSED, LANG_FRENCH } from '@/constants';
import { getCurrentLocale } from '@/i18n';
import { DateTime } from 'luxon';
import cloneDeep from 'lodash/cloneDeep';
import { resetTimer } from '@/utils/dateUtils';
import { useComponentComposable } from '@/components/useComponentComposable';
import { usePackageManagementStore } from '@/store/PackageManagementStore';
import AutoComplete, { AutoCompleteCompleteEvent } from 'primevue/autocomplete';
import { useToast } from 'primevue/usetoast';
import Button from 'primevue/button';
import DatePicker from 'primevue/datepicker';
import Fluid from 'primevue/fluid';
import InputText from 'primevue/inputtext';
import MultiSelect from 'primevue/multiselect';
import ToggleSwitch from 'primevue/toggleswitch';
import LoadingSpinner from '@/components/LoadingSpinner.vue';
import { TDuration } from './GatewayDestinationDuration.vue';

const dialogRef = inject('dialogRef') as Ref;
const onSave = dialogRef?.value.options.emits.onSave;
const dataEdit = dialogRef?.value?.options?.data?.data;
const gateways = dialogRef?.value?.options?.data?.gateways;
const destinationList = dialogRef?.value?.options?.data?.destinationList;

const { t } = useI18n({ useScope: 'global' });
const toast = useToast();
const PackageManagementStore = usePackageManagementStore();
const { checkValidateListDateRange } = useComponentComposable();
const destinationCode = ref('');

const today = ref(new Date());

const formatListDate = (listDate: any) => {
  if (listDate?.length > 0) {
    return listDate.map((date: any) => {
      return {
        start: new Date(DateTime.fromISO(date.start)),
        end: new Date(DateTime.fromISO(date.end))
      };
    });
  } else {
    return [];
  }
};

const formatData = (data: any) => {
  return {
    ...data,
    travelDate: formatListDate(data.travelDate) || [],
    expiryDate: new Date(DateTime.fromISO(data.expiryDate))
  };
};

const data = ref<TDuration>(
  dataEdit
    ? cloneDeep(formatData(dataEdit))
    : {
        gatewayCode: '',
        gatewayName: '',
        destinationSVId: '',
        destinationName: '',
        destinationAirportCode: '',
        cacheDuration: '',
        additionalDuration: [],
        expiryDate: null,
        travelDate: [],
        status: STATUS_CLOSED
      }
);

const filteredGateways = ref<any[]>([]);
const filteredDestinations = ref<any[]>([]);
const listDestDuration = ref<any>({});
const cacheDurationFilter = ref<number[]>([]);
const currentLanguage = getCurrentLocale();
const isLoading = ref(false);

const onFilterGateway = async (event: AutoCompleteCompleteEvent) => {
  if (!event.query.trim().length) {
    filteredGateways.value = [...gateways];
  } else {
    filteredGateways.value = gateways.filter((gateway: any) => {
      return (
        gateway.code?.toLowerCase().startsWith(event.query.toLowerCase()) ||
        gateway.airportName?.toLowerCase().startsWith(event.query.toLowerCase()) ||
        gateway.name?.toLowerCase().startsWith(event.query.toLowerCase()) ||
        gateway.region?.toLowerCase().startsWith(event.query.toLowerCase()) ||
        gateway.province?.toLowerCase().startsWith(event.query.toLowerCase()) ||
        gateway.countryName?.toLowerCase().startsWith(event.query.toLowerCase())
      );
    });
  }
};

const onFilterDestination = async (event: AutoCompleteCompleteEvent) => {
  if (!event.query.trim().length) {
    filteredDestinations.value = [...destinationList];
  } else {
    filteredDestinations.value = destinationList.filter((air: any) => {
      return air.code?.toLowerCase().startsWith(event.query.toLowerCase()) || air.name?.toLowerCase().startsWith(event.query.toLowerCase()) || air.name?.toLowerCase().startsWith(event.query.toLowerCase());
    });
  }
};

const selectedDestination = (e: any) => {
  cacheDurationFilter.value = getCacheDuration(e.value?.code, listDestDuration.value);
  data.value.cacheDuration = cacheDurationFilter.value?.join(',') ?? '';
  data.value.destinationAirportCode = e.value?.airportCode;
  data.value.destinationName = e.value?.name;
  destinationCode.value = e.value?.code;
  data.value.additionalDuration = [];
};

const getCacheDuration = (destCode: string, listDurations: any) => {
  if (destCode) {
    return listDurations[destCode];
  } else {
    return [];
  }
};

const onAddTravelDate = () => {
  data.value.travelDate.push({ start: null, end: null });
};

const onRemoveTravelDate = (id: number) => {
  const newData = cloneDeep(data.value.travelDate).filter((item: any, index: any) => index !== id);
  data.value.travelDate = newData;
};

const selectedGateway = (e: any) => {
  data.value.gatewayName = e.value?.name;
  listDestDuration.value = e.value?.destDurations ?? {};
  cacheDurationFilter.value = getCacheDuration(destinationCode.value, listDestDuration.value);
  data.value.cacheDuration = cacheDurationFilter.value?.join(',') ?? '';
  data.value.additionalDuration = [];
};

const getAdditionalDuration = (cacheDurations: string[], additionalDurations: string[]) => {
  return additionalDurations.filter((additionalDuration: string) => !cacheDurations.includes(additionalDuration));
};

const formatDateZeroHour = (listDate: any) => {
  if (listDate?.length) {
    return listDate?.map((date: any) => {
      return {
        start: resetTimer(date.start),
        end: resetTimer(date.end)
      };
    });
  } else {
    return [];
  }
};

const submit = (destinationDuration: any) => {
  const cacheDuration = destinationDuration?.cacheDuration?.split(',');
  const additionalDuration = getAdditionalDuration(cacheDuration, destinationDuration?.additionalDuration);
  const payload = {
    ...destinationDuration,
    destinationSVId: destinationDuration.destinationSVId.code,
    gatewayCode: destinationDuration.gatewayCode.code,
    travelDate: formatDateZeroHour(destinationDuration?.travelDate),
    expiryDate: resetTimer(destinationDuration?.expiryDate),
    cacheDuration,
    additionalDuration
  };
  try {
    if (!checkValidateListDateRange(payload.travelDate)) {
      toast.add({ severity: 'error', summary: '', detail: t('notification.please_select_date'), life: 5000 });
    } else if (dataEdit) {
      PackageManagementStore.updateGatewayDuration(payload).then(() => {
        toast.add({ severity: 'success', summary: '', detail: t('notification.save_successfully'), life: 3000 });
        onSave();
      });
    } else {
      PackageManagementStore.addGatewayDuration(payload).then(() => {
        toast.add({ severity: 'success', summary: '', detail: t('notification.save_successfully'), life: 3000 });
        onSave();
      });
    }
  } catch {
    toast.add({ severity: 'error', summary: '', detail: t('notification.save_error'), life: 5000 });
  }
};

const filterDestination = (destinationCode: number) => {
  return destinationList?.find((destination) => destination?.code?.toString() === destinationCode?.toString());
};

const filteredGatewayByCode = (gatewayCode: string) => {
  return gateways?.find((gateway: any) => gateway?.code === gatewayCode);
};

const showDataEdit = (dataEdit: any) => {
  if (dataEdit?.gatewayCode) {
    data.value.gatewayCode = filteredGatewayByCode(dataEdit.gatewayCode);
    listDestDuration.value = data.value.gatewayCode?.destDurations ?? {};
    filteredDestinations.value = destinationList;
    data.value.destinationSVId = filterDestination(dataEdit.destinationSVId);
    destinationCode.value = filterDestination(dataEdit.destinationSVId)?.code;
    cacheDurationFilter.value = dataEdit.cacheDuration?.map(Number);
    data.value.cacheDuration = dataEdit.cacheDuration?.join(',');
  }
};

const clearGateway = () => {
  data.value.gatewayCode = '';
  data.value.gatewayName = '';
  data.value.cacheDuration = '';
  listDestDuration.value = {};
  cacheDurationFilter.value = [];
  data.value.additionalDuration = [];
};

const clearDestination = () => {
  data.value.destinationSVId = '';
  data.value.destinationName = '';
  data.value.destinationAirportCode = '';
  data.value.cacheDuration = '';
  destinationCode.value = '';
  data.value.additionalDuration = [];
  cacheDurationFilter.value = [];
};

const filterOption = () => {
  let listAdditionalDuration = [];
  listAdditionalDuration = Array.from({ length: 30 }, (_, i) => ({ label: `${i + 1}`, value: (i + 1).toString() }));
  return listAdditionalDuration;
};

onMounted(() => {
  if (dataEdit) {
    showDataEdit(dataEdit);
  }
});
</script>

<template>
  <div>
    <LoadingSpinner v-if="isLoading" />
    <div class="manage-gateway-duration-modal">
      <Fluid>
        <label for="gateway_name" class="mb-1 inline-block">{{ `${$t('advance_quote.form.gateway')}` }}</label>
        <div class="flex align-content-start gap-3">
          <div class="field flex-1">
            <div class="flex align-items-center">
              <AutoComplete
                v-model="data.gatewayCode"
                optionValue="airportName"
                :suggestions="filteredGateways"
                @complete="onFilterGateway($event)"
                completeOnFocus
                optionLabel="airportName"
                @change="selectedGateway($event)"
                class="w-full"
                id="gateway_name"
                forceSelection
              >
                <template #option="slotProps">
                  <div class="flex align-options-center">
                    <div>
                      <div>{{ slotProps.option.airportName }} ({{ slotProps.option.code }})</div>
                      <div class="text-xs">{{ slotProps.option.name }}, {{ slotProps.option.region || slotProps.option.province }}, {{ slotProps.option.countryName }}</div>
                    </div>
                  </div>
                </template>
              </AutoComplete>
              <div>
                <i class="pi pi-times-circle ml-2 text-red-500 cursor-pointer" data-testid="i_clear_gateway" @click="clearGateway()"></i>
              </div>
            </div>
          </div>
        </div>
      </Fluid>
      <Fluid>
        <label for="duration_sv_destination" class="mb-1 inline-block">{{ `${$t('management.package.sv_destination')}` }}</label>
        <div class="flex align-content-start gap-3">
          <div class="field flex-1">
            <div class="flex align-items-center">
              <AutoComplete
                class="w-full"
                v-model="data.destinationSVId"
                :suggestions="filteredDestinations"
                @complete="onFilterDestination($event)"
                optionLabel="code"
                optionValue="code"
                id="duration_sv_destination"
                completeOnFocus
                @change="selectedDestination($event)"
                forceSelection
              >
                <template #option="slotProps">
                  <div>{{ currentLanguage === LANG_ENGLISH ? `${slotProps.option.code} - ${slotProps.option.name} (${slotProps.option.country})` : `${slotProps.option.code} - ${slotProps.option.nameFr} (${slotProps.option.countryFr})` }}</div>
                </template>
              </AutoComplete>
              <div>
                <i class="pi pi-times-circle ml-2 text-red-500 cursor-pointer" data-testid="i_clear_destination" @click="clearDestination()"></i>
              </div>
            </div>
          </div>
        </div>
      </Fluid>
      <Fluid>
        <div class="field grid">
          <div class="col col-4">
            <label for="duration_destination_airport_code">{{ $t('report.destination_airport_code') }}</label>
            <InputText data-testid="duration_destination_airport_code" id="duration_destination_airport_code" v-model="data.destinationAirportCode" disabled />
          </div>
          <div class="col col-8">
            <label for="destination_name" :class="[{ 'special-field': currentLanguage === LANG_FRENCH }]">{{ $t('management.package.destination_name') }}</label>
            <InputText id="destination_name" v-model="data.destinationName" disabled />
          </div>
        </div>
      </Fluid>
      <Fluid>
        <div class="field grid">
          <div class="col col-4">
            <label for="duration_destination_cachedDuration">{{ $t('management.package.cached_duration') }}</label>
            <InputText id="duration_destination_cachedDuration" v-model="data.cacheDuration" disabled />
          </div>
          <div class="col col-8">
            <label for="duration_destination_addDuration">{{ $t('management.package.additional_duration') }}</label>
            <MultiSelect
              id="duration_destination_addDuration"
              display="chip"
              v-model="data.additionalDuration"
              :placeholder="$t('management.package.additional_duration')"
              :options="filterOption()"
              optionLabel="label"
              optionValue="value"
              class="w-full"
            >
              <template #option="slotProps">
                <p>
                  {{ slotProps.option.label }}
                </p>
              </template>
            </MultiSelect>
          </div>
        </div>
        <div>
          <p class="text-600 text-xs mb-1 hint font-italic text-primary">* {{ $t('management.package.hint') }}</p>
        </div>
      </Fluid>
      <Fluid>
        <div class="field grid">
          <div class="col col-12">
            <label for="duration_expiry_date">{{ $t('worksheet.expiry_date') }}</label>
            <DatePicker id="duration_expiry_date" dateFormat="dd-M-yy" :minDate="today" v-model="data.expiryDate" autocomplete="off" class="w-full" :placeholder="$t('common.date_format')">
              <template #footer>
                <p class="text-center">
                  <small>{{ $t('common.press_space') }}</small>
                </p>
              </template>
            </DatePicker>
          </div>
        </div>
      </Fluid>
      <Fluid>
        <div class="field grid">
          <div class="col">
            <div class="flex align-content-center gap-4">
              <p>{{ $t('hotel_contract.travel_date_range') }}</p>
              <p class="text-red-500 cursor-pointer" @click="onAddTravelDate()">+{{ $t('common.add') }}</p>
            </div>
            <div class="grid field mt-2" v-for="(child, i) in data.travelDate" :key="i">
              <div class="col col-6">
                <label for="start_date">{{ $t('create_quote.start_date') }}</label>
                <DatePicker id="start_date" dateFormat="dd-M-yy" v-model="child.start" autocomplete="off" class="w-full" :placeholder="$t('common.date_format')" :minDate="new Date()">
                  <template #footer>
                    <p class="text-center">
                      <small>{{ $t('common.press_space') }}</small>
                    </p>
                  </template>
                </DatePicker>
              </div>
              <div class="col col-6">
                <div class="flex">
                  <div class="flex-grow-1">
                    <label for="end_date">{{ $t('create_quote.end_date') }}</label>
                    <DatePicker id="end_date" dateFormat="dd-M-yy" v-model="child.end" autocomplete="off" class="w-full" :placeholder="$t('common.date_format')" :minDate="new Date(child.start)">
                      <template #footer>
                        <p class="text-center">
                          <small>{{ $t('common.press_space') }}</small>
                        </p>
                      </template>
                    </DatePicker>
                  </div>
                  <div>
                    <i v-if="data.travelDate.length > 0" class="pi pi-times-circle text-primary text-xl ml-1 mt-5 cursor-pointer" @click="onRemoveTravelDate(i)"></i>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </Fluid>
      <Fluid>
        <div class="field grid">
          <div class="col col-12">
            <div class="flex gap-3">
              <div>{{ $t('common.status') }}</div>
              <ToggleSwitch v-model="data.status" :trueValue="STATUS_OPEN" :falseValue="STATUS_CLOSED" />
            </div>
          </div>
        </div>
      </Fluid>
      <div class="flex justify-content-center mt-3">
        <Button :disabled="!data.gatewayCode || !data.destinationSVId || !data.expiryDate" :label="$t('voucher.submit')" @click="submit(data)" data-testid="btn_submit" />
      </div>
    </div>
  </div>
</template>
